import { ThemeColor } from '@/enums'
import { BurgerLine, PhoneLine, SureinLogoNew } from '@/icons'
import { useAuth, useContactModal } from '@/providers'
import { paths } from '@/routes/paths'
import { isVerified, useGoToPath } from '@/utils'
import { Button, useSidebar } from '@surein/ui'
import { useLocation } from 'react-router-dom'
import { IconButton, LanguageSwitch } from '../../atoms'
import { ShowWhen } from '../../hoc/ShowWhen'
import { Relative, Row } from '../../styled'
import { Container } from './styles'

interface HideContact {
  chat?: boolean
  header?: boolean
}

export interface HeaderBaseProps {
  backgroundColor?: string
  hasHeadquarters?: boolean
  hidden?: boolean
  border?: boolean
  onLogoClick?: () => void
  hide?: {
    contact?: HideContact
    language?: boolean
    menu?: boolean
  }
}

export const HeaderBase = ({
  backgroundColor,
  hasHeadquarters,
  hidden,
  border,
  onLogoClick,
  hide = {},
}: HeaderBaseProps) => {
  // @ts-expect-error this will go away after migration of useContactModal
  const { openContact } = useContactModal({
    hasHeadquarters,
    hide,
  })
  const { toggleSidebar, openMobile: isOpenMobile } = useSidebar()

  return (
    <Container
      backgroundColor={backgroundColor}
      border={border}
      hidden={hidden}
      isOpenMobile={isOpenMobile}
    >
      <Relative>
        <Button
          variant="ghost"
          onClick={onLogoClick}
          style={{ padding: 0, background: 'transparent' }}
        >
          <SureinLogoNew height={24} width={88} />
        </Button>
      </Relative>
      <Row alignItems="center" gap="8px">
        <ShowWhen when={!hide.language}>
          <LanguageSwitch size="medium" />
        </ShowWhen>
        <IconButton
          aria-label="contact button"
          onClick={openContact}
          size="medium"
          variant="outlined"
        >
          <PhoneLine color={ThemeColor.b0} size="info" />
        </IconButton>
        <ShowWhen when={!hide.menu}>
          <IconButton onClick={toggleSidebar} size="medium" variant="outlined">
            <BurgerLine size="info" stroke />
          </IconButton>
        </ShowWhen>
      </Row>
    </Container>
  )
}

interface HeaderProps {
  backgroundColor?: string
  hasHeadquarters?: boolean
  hidden?: boolean
  border?: boolean
  onLogoClick?: () => void
  hideContact?: HideContact
  shouldShowSidebar?: boolean
  simple?: boolean
}

export const Header = ({
  backgroundColor,
  hasHeadquarters,
  hidden,
  border,
  onLogoClick,
  hideContact,
  simple,
  shouldShowSidebar,
}: HeaderProps) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { pathname } = useLocation()

  const goToRoot = useGoToPath(paths.authRedirect)

  const shouldRenderMenu =
    isAuthenticated && isVerified(user) && !simple && shouldShowSidebar

  const shouldRenderLanguage =
    !isLoading &&
    (!isAuthenticated ||
      pathname.includes('assessment') ||
      pathname.includes('funnel'))

  return (
    <HeaderBase
      backgroundColor={backgroundColor}
      border={border}
      hasHeadquarters={hasHeadquarters}
      hidden={hidden}
      hide={{
        contact: hideContact,
        language: !shouldRenderLanguage,
        menu: !shouldRenderMenu,
      }}
      onLogoClick={onLogoClick || goToRoot}
    />
  )
}
